import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatEuro, STUDIO_LABELS, STUDIO_SUBTITLES, STUDIO_LOGOS, MEDIA_CATEGORY_LABELS } from "@/lib/api";
import { SITE, IS_STUDIO, SITE_STUDIO_TYPE } from "@/config";
import { SocialBubbles, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/components/SocialLinks";
import { OldPrice, PromoBadge } from "@/components/PromoPrice";
import { useSiteTexts } from "@/hooks/useSiteTexts";
import {
  Mic,
  Camera,
  Image as ImageIcon,
  Video,
  Radio,
  Briefcase,
  Disc3,
  Layers,
  Crown,
  Wand2,
  SlidersHorizontal,
  CalendarDays,
  Clock,
  ShieldCheck,
  ArrowRight,
  Check,
  GraduationCap,
  MapPin,
  Mail,
  Phone,
  Music,
  PenLine,
  Lightbulb,
  Info,
  Film,
  Users,
  Plane,
  Clapperboard,
  Heart,
  Plus,
} from "lucide-react";

const SERVICE_ICONS = {
  mic: Mic,
  camera: Camera,
  image: ImageIcon,
  video: Video,
  radio: Radio,
  briefcase: Briefcase,
  disc: Disc3,
  layers: Layers,
  crown: Crown,
  wand: Wand2,
  sliders: SlidersHorizontal,
  music: Music,
  pen: PenLine,
  lightbulb: Lightbulb,
  film: Film,
  users: Users,
  plane: Plane,
  clapperboard: Clapperboard,
};

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] },
};

const POST_PROD_SLUGS = ["mixage", "mastering", "mix-master"];

const GALLERY_ITEMS = [
  {
    src: "https://images.pexels.com/photos/5642756/pexels-photo-5642756.jpeg?auto=compress&cs=tinysrgb&w=900",
    label: "Clip urbain de nuit",
    tall: true,
  },
  {
    src: "https://images.unsplash.com/photo-1485662543942-9ab24cc0acf0?auto=format&fit=crop&w=900&q=80",
    label: "Tournage caméra épaule",
  },
  {
    src: "https://images.pexels.com/photos/29261299/pexels-photo-29261299.jpeg?auto=compress&cs=tinysrgb&w=900",
    label: "Film de mariage",
    tall: true,
  },
  {
    src: "https://images.pexels.com/photos/7383469/pexels-photo-7383469.jpeg?auto=compress&cs=tinysrgb&w=900",
    label: "Studio podcast",
  },
  {
    src: "https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?auto=format&fit=crop&w=900&q=80",
    label: "Séance photo studio",
  },
  {
    src: "https://images.pexels.com/photos/33365000/pexels-photo-33365000.jpeg?auto=compress&cs=tinysrgb&w=900",
    label: "Plateau de tournage",
    tall: true,
  },
  {
    src: "https://images.unsplash.com/photo-1495764506633-93d4dfed7c6b?auto=format&fit=crop&w=900&q=80",
    label: "Prises de vue drone",
  },
  {
    src: "https://images.unsplash.com/photo-1497015289639-54688650d173?auto=format&fit=crop&w=900&q=80",
    label: "Vidéo corporate",
  },
];

const TIER_DISPLAY_ORDER = ["1", "2", "3", "4", "5", "6", "10", "20"];

const PriceRow = ({ svc }) => {
  if (svc.price_tiers) {
    return (
      <div>
        {(svc.from_price || svc.promo_live) && (
          <p className="mb-1.5 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-primary">
            {svc.from_price && <span>À partir de</span>}
            <PromoBadge svc={svc} />
          </p>
        )}
        <div className="grid grid-cols-3 gap-1.5" data-testid={`price-grid-${svc.slug}`}>
          {TIER_DISPLAY_ORDER.filter((k) => svc.price_tiers[k] !== undefined).map((k) => (
            <div
              key={k}
              className="rounded-md border border-primary/25 bg-[hsl(var(--studio-yellow)/0.08)] px-2 py-1.5 text-center"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{k}h</p>
              <p className="font-mono-nums text-sm font-semibold text-primary">
                {svc.promo_live && svc.price_tiers_before && (
                  <s className="mr-1 text-[10px] font-normal text-muted-foreground">
                    {formatEuro(svc.price_tiers_before[k])}
                  </s>
                )}
                {formatEuro(svc.price_tiers[k])}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Autres durées : combinaison la plus avantageuse des paliers. Même tarif en ligne ou sur
          place.
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-md bg-[hsl(var(--studio-yellow)/0.10)] px-3 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-primary">Tarif</span>
      <span className="font-mono-nums font-semibold text-primary">
        {svc.from_price && (
          <span className="mr-1 text-[11px] font-normal text-muted-foreground">à partir de</span>
        )}
        <OldPrice svc={svc} />
        {formatEuro(svc.price_onsite)}
        {svc.pricing_type === "hourly" ? "/h" : ""}
        <PromoBadge svc={svc} className="ml-1.5" />
        <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">en ligne ou sur place</span>
      </span>
    </div>
  );
};

const GALLERY_THEME_ORDER = ["teaser", "mariage", "clip", "photo", "book", "podcast", "corporate", "realisation", "autre"];

export default function Home() {
  const texts = useSiteTexts();
  const [services, setServices] = useState([]);
  const [discount, setDiscount] = useState(10);
  const [siteMedia, setSiteMedia] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState("tous");
  const location = useLocation();

  useEffect(() => {
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
    api
      .get("/settings/public")
      .then((res) => {
        setDiscount(res.data.online_discount_percent);
      })
      .catch(() => {});
    if (!IS_STUDIO) {
      api.get("/media").then((res) => setSiteMedia(res.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }
  }, [location.hash, services.length]);

  const studioServices = services.filter((s) => s.category === "studio");
  const complements = services.filter((s) => s.category === "complement");
  const beatCatalogue = services.filter((s) => s.category === "beatmaking_catalogue");
  const weddingFormules = services.filter((s) => s.category === "mariage");
  const weddingOptions = services.filter((s) => s.category === "mariage_option");
  const mediaSupplements = services.filter((s) => s.category === "media_supplement");
  const realisationMedia = siteMedia.filter((m) => m.category !== "casting");
  const castingMedia = siteMedia.filter((m) => m.category === "casting");
  const galleryThemes = GALLERY_THEME_ORDER.filter((t) =>
    realisationMedia.some((m) => m.category === t)
  );
  const filteredGalleryMedia =
    galleryFilter === "tous"
      ? realisationMedia
      : realisationMedia.filter((m) => m.category === galleryFilter);

  const mediaSrc = (url) => (url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

  const isEmbedLink = (url) => url.includes("youtube.com/embed") || url.includes("player.vimeo.com");

  const linkPlatform = (url) => {
    if (url.includes("instagram.com")) return "Instagram";
    if (url.includes("tiktok.com")) return "TikTok";
    if (url.includes("snapchat.com")) return "Snapchat";
    return "le lien";
  };

  const renderMedia = (item, extraClass = "") => {
    if (item.media_type === "photo") {
      return (
        <img
          src={mediaSrc(item.url)}
          alt={item.title || "réalisation FNK FILMS"}
          loading="lazy"
          className={`w-full object-cover transition-transform duration-300 group-hover:scale-[1.05] ${extraClass}`}
        />
      );
    }
    if (item.source === "link" && isEmbedLink(item.url)) {
      return (
        <iframe
          src={mediaSrc(item.url)}
          title={item.title || "vidéo FNK FILMS"}
          className={`w-full ${extraClass}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (item.source === "link") {
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-black via-neutral-900 to-black text-white transition-colors hover:from-neutral-900 ${extraClass}`}
          data-testid={`media-link-out-${item.id}`}
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <ArrowRight className="h-5 w-5 text-primary-foreground" />
          </span>
          <span className="px-3 text-center text-xs font-semibold">
            Voir sur {linkPlatform(item.url)} ↗
          </span>
        </a>
      );
    }
    return <video src={mediaSrc(item.url)} controls className={`w-full bg-black object-contain ${extraClass}`} />;
  };

  return (
    <div>
      {/* HERO */}
      <section className="noise-bg hero-signal border-b border-border">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
            <motion.div
              className="md:col-span-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Badge variant="outline" className="border-primary/40 text-primary">
                {texts.hero_badge || "Ouvert 24h/24 — 7j/7"}
              </Badge>
              <h1 className="mt-5 font-heading text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                {texts.hero_title_a || SITE.heroTitleA}{" "}
                <span className="text-primary">{texts.hero_title_accent_a || SITE.heroTitleAccentA}</span>
                <br />
                {texts.hero_title_b || SITE.heroTitleB}
                <br />
                <span className="text-primary">{texts.hero_title_accent_b || SITE.heroTitleAccentB}</span>
              </h1>
              <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
                <span className="font-medium text-foreground">{SITE.fullName}</span> — {texts.hero_text || SITE.heroText}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button size="lg" asChild data-testid="hero-booking-cta">
                  <Link to="/reserver">
                    <CalendarDays className="mr-2 h-5 w-5" />
                    Réserver un créneau
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild data-testid="hero-planning-cta">
                  <Link to="/planning">Voir le planning</Link>
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" /> 24h/24 – 7j/7
                </span>
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" /> Paiement sécurisé
                </span>
                {CONTACT_PHONE && (
                  <a href={CONTACT_PHONE_HREF} className="flex items-center gap-2 hover:text-primary" data-testid="hero-phone-link">
                    <Phone className="h-4 w-4 text-primary" /> {CONTACT_PHONE}
                  </a>
                )}
              </div>
              <div
                className="mt-6 inline-flex items-center gap-2.5 rounded-lg border border-primary/40 bg-[hsl(var(--studio-yellow)/0.08)] px-4 py-3"
                data-testid="studio-address"
              >
                <MapPin className="h-5 w-5 flex-shrink-0 text-primary" />
                <span className="text-sm font-semibold sm:text-base">
                  47 avenue de la République, 69200 Vénissieux
                </span>
              </div>
            </motion.div>
            <motion.div
              className="md:col-span-6"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <div className="overflow-hidden rounded-xl border-2 border-primary/30">
                <img src={SITE.heroImg} alt={SITE.heroAlt} className="aspect-[4/3] w-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ESPACE DU SITE */}
      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <motion.div {...fadeUp}>
          <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            {texts.espace_title || "Un espace dédié, un planning en créneaux d'une heure"}
          </h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            {texts.espace_subtitle || "Consultez les disponibilités en temps réel et réservez votre créneau, 24h/24 – 7j/7."}
          </p>
        </motion.div>
        <div className="mt-8">
          <motion.div {...fadeUp}>
            <Card
              className="group h-full overflow-hidden border-border bg-card transition-colors duration-200 hover:border-primary/50"
              data-testid={`space-card-${SITE_STUDIO_TYPE}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative h-56 overflow-hidden bg-black md:h-full">
                  <img
                    src={SITE.logo}
                    alt={`Logo ${SITE.fullName}`}
                    className="mx-auto h-full object-contain py-3 transition-transform duration-300 group-hover:scale-[1.04]"
                    data-testid={`space-logo-${SITE_STUDIO_TYPE}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                      {IS_STUDIO ? (
                        <Mic className="h-5 w-5 text-primary-foreground" />
                      ) : (
                        <Camera className="h-5 w-5 text-primary-foreground" />
                      )}
                    </span>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">{SITE.fullName}</h3>
                      <p className="text-xs text-white/70">{SITE.subtitle}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 sm:p-6">
                  <ul className="space-y-2">
                    {studioServices.map((svc) => (
                      <li key={svc.id} className="flex items-center justify-between text-sm">
                        <span className="text-foreground/90">{svc.name}</span>
                        <span className="font-mono-nums text-primary">
                          {svc.from_price && (
                            <span className="mr-1 text-[11px] text-muted-foreground">à partir de</span>
                          )}
                          <OldPrice svc={svc} online />
                          {formatEuro(svc.price_online)}
                          {svc.pricing_type === "hourly" ? "/h" : ""}
                          <PromoBadge svc={svc} className="ml-1.5" />
                        </span>
                      </li>
                    ))}
                    {IS_STUDIO && (
                      <li className="flex items-center justify-between text-sm">
                        <a href="#beatmaking" className="text-primary underline-offset-2 hover:underline">
                          Beatmaking : licences &amp; exclusivités
                        </a>
                        <ArrowRight className="h-4 w-4 text-primary" />
                      </li>
                    )}
                  </ul>
                  <div className="mt-5 flex gap-3">
                    <Button asChild className="flex-1" data-testid={`space-book-${SITE_STUDIO_TYPE}`}>
                      <Link to="/reserver">
                        Réserver
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" asChild data-testid={`space-planning-${SITE_STUDIO_TYPE}`}>
                      <Link to="/planning">Planning</Link>
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="border-y border-border bg-[hsl(var(--studio-ink))] noise-bg">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
              {texts.tarifs_title || "Tarifs & services"}
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              {texts.tarifs_subtitle ||
                "Payez en ligne à l'avance pour confirmer immédiatement votre créneau, ou réservez et réglez sur place (espèces ou carte bancaire) — même tarif."}
            </p>
            {!IS_STUDIO && (
              <div
                className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
                data-testid="location-plateau-notice"
              >
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <p className="text-sm text-foreground/90">
                  <span className="font-semibold text-primary">Location du plateau :</span> le local
                  (fond vert, éclairages, plateau podcast…) se loue{" "}
                  <span className="font-medium">avec ou sans nos caméras</span> — sans montage de
                  notre part, le tarif est plus élevé avec nos caméras professionnelles incluses.{" "}
                  <span className="font-medium text-primary">
                    Montage disponible en supplément sur demande
                  </span>{" "}
                  — précisez-le lors de votre réservation ou par email.
                </p>
              </div>
            )}
          </motion.div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {studioServices.map((svc) => {
              const Icon = SERVICE_ICONS[svc.icon] || Mic;
              return (
                <motion.div key={svc.id} {...fadeUp}>
                  <Card
                    className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                    data-testid={`service-card-${svc.slug}`}
                  >
                    <CardContent className="flex h-full flex-col p-5 sm:p-6">
                      <div className="flex items-start justify-between">
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                        <Badge variant="outline" className="border-border text-muted-foreground">
                          {STUDIO_LABELS[svc.studio_type]}
                        </Badge>
                      </div>
                      <h3 className="mt-4 font-heading text-lg font-semibold">{svc.name}</h3>
                      <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{svc.description}</p>
                      {(svc.features || []).length > 0 && (
                        <ul className="mt-3 space-y-1.5" data-testid={`service-features-${svc.slug}`}>
                          {svc.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs text-foreground/85">
                              <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-5">
                        <PriceRow svc={svc} />
                      </div>
                      {svc.pricing_type === "fixed" && svc.duration_hours && (
                        <p className="mt-3 text-xs text-muted-foreground">Durée : {svc.duration_hours}h</p>
                      )}
                      {!IS_STUDIO && (
                        <p
                          className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground"
                          data-testid={`supplements-hint-${svc.slug}`}
                        >
                          <Plus className="mt-0.5 h-3 w-3 flex-shrink-0 text-primary" />
                          Suppléments au choix lors de la réservation : figurant(e)s, danseur(se)s,
                          garage auto.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUPPLEMENTS TOURNAGE (site films uniquement) */}
      {!IS_STUDIO && mediaSupplements.length > 0 && (
        <section id="supplements" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
          <motion.div {...fadeUp}>
            <Badge variant="outline" className="border-primary/40 text-primary">
              {texts.supplements_badge || "Suppléments tournage"}
            </Badge>
            <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
              {texts.supplements_title || "Donnez du caractère à vos clips"}
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              {texts.supplements_subtitle ||
                "Des décors et éléments exclusifs à ajouter à votre clip, séance photo, book ou vidéo."}
            </p>
            <div
              className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
              data-testid="supplements-notice"
            >
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <p className="text-sm text-foreground/90">
                <span className="font-semibold text-primary">En supplément :</span> ces options
                s'ajoutent à une prestation (clip, photo, book, vidéo…) —{" "}
                <span className="font-medium">sélectionnez-les directement lors de votre réservation</span>{" "}
                en ligne. Pour les figurants et danseurs, choisissez le nombre de personnes.
                <br />
                <span className="font-semibold text-primary">Profils spécifiques :</span> vous cherchez
                des figurants particuliers ou un danseur / une danseuse différente ? Écrivez-nous à{" "}
                <a
                  href="mailto:fnkfilms.pro@gmail.com"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  data-testid="supplements-specific-email-link"
                >
                  fnkfilms.pro@gmail.com
                </a>{" "}
                avec la description des profils recherchés — nous organisons le casting.
              </p>
            </div>
          </motion.div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {mediaSupplements.map((svc) => {
              const Icon = SERVICE_ICONS[svc.icon] || Film;
              return (
                <motion.div key={svc.id} {...fadeUp}>
                  <Card
                    className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                    data-testid={`supplement-card-${svc.slug}`}
                  >
                    <CardContent className="flex h-full flex-col p-5 sm:p-6">
                      <div className="flex items-start justify-between">
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                        <Badge variant="outline" className="border-primary/40 text-[11px] text-primary">
                          En supplément
                        </Badge>
                      </div>
                      <h3 className="mt-4 font-heading text-lg font-semibold">{svc.name}</h3>
                      <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{svc.description}</p>
                      <p className="mt-4 font-mono-nums text-xl font-semibold text-primary">
                        +{" "}
                        {svc.from_price && (
                          <span className="text-xs font-normal text-muted-foreground">à partir de </span>
                        )}
                        <OldPrice svc={svc} />
                        {formatEuro(svc.price_onsite)}
                        <PromoBadge svc={svc} className="ml-1.5" />
                        {(svc.slug === "supplement-figurant" || svc.slug === "supplement-danseur") && (
                          <span className="ml-1 text-xs font-normal text-muted-foreground">/ personne</span>
                        )}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* NOS DANSEURS & FIGURANTS (site films uniquement, si médias casting) */}
      {!IS_STUDIO && castingMedia.length > 0 && (
        <section id="casting" className="mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6 sm:pb-16 lg:px-8">
          <motion.div {...fadeUp}>
            <Badge variant="outline" className="border-primary/40 text-primary">
              <Users className="mr-1.5 h-3.5 w-3.5" />
              {texts.casting_badge || "Casting"}
            </Badge>
            <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
              {texts.casting_title || "Nos danseurs & figurants"}
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              {texts.casting_subtitle ||
                "Un aperçu des talents disponibles pour vos clips, photos et vidéos — ajoutez-les en supplément lors de votre réservation."}
            </p>
          </motion.div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {castingMedia.map((item) => (
              <motion.div key={item.id} {...fadeUp}>
                <div
                  className="group overflow-hidden rounded-xl border border-primary/20 bg-black"
                  data-testid={`casting-media-${item.id}`}
                >
                  <div className="relative">
                    {renderMedia(item, item.media_type === "photo" ? "aspect-[3/4]" : "aspect-video")}
                  </div>
                  {item.title && (
                    <p className="border-t border-primary/20 bg-black/80 px-3 py-2 text-xs font-semibold text-white">
                      {item.title}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* MARIAGES (site films uniquement) */}
      {!IS_STUDIO && weddingFormules.length > 0 && (
        <section id="mariage" className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <motion.div {...fadeUp}>
            <Badge variant="outline" className="border-primary/40 text-primary">
              <Heart className="mr-1.5 h-3.5 w-3.5" />
              {texts.mariage_badge || "Mariages"}
            </Badge>
            <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
              {texts.mariage_title || "Films de mariage : le plus beau jour de votre vie, en images"}
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              {texts.mariage_subtitle ||
                "Une ou plusieurs caméras, drone, montage cinéma… Nous immortalisons votre journée avec l'exigence d'un tournage professionnel et la discrétion qu'exige votre événement."}
            </p>
          </motion.div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {weddingFormules.map((svc) => {
              const Icon = SERVICE_ICONS[svc.icon] || Video;
              const highlight = svc.slug === "mariage-complet";
              return (
                <motion.div key={svc.id} {...fadeUp}>
                  <Link
                    to={`/devis-mariage?formule=${encodeURIComponent(svc.name)}`}
                    className="block h-full"
                    data-testid={`wedding-card-link-${svc.slug}`}
                  >
                    <Card
                      className={`h-full cursor-pointer bg-card transition-colors duration-200 hover:border-primary ${
                        highlight ? "border-primary/50" : "border-border"
                      }`}
                      data-testid={`wedding-card-${svc.slug}`}
                    >
                      <CardContent className="flex h-full flex-col p-5 sm:p-6">
                        <div className="flex items-start justify-between">
                          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                            <Icon className="h-5 w-5 text-primary" />
                          </span>
                          {highlight && (
                            <Badge className="bg-primary text-primary-foreground">La plus choisie</Badge>
                          )}
                        </div>
                        <h3 className="mt-4 font-heading text-lg font-semibold">{svc.name}</h3>
                        <p className="mt-1 text-sm text-muted-foreground">{svc.description}</p>
                        <ul className="mt-4 flex-1 space-y-1.5">
                          {(svc.features || []).map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs text-foreground/85">
                              <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-5 font-mono-nums text-2xl font-semibold text-primary">
                          {svc.from_price && (
                            <span className="mr-1.5 text-sm font-normal text-muted-foreground">
                              à partir de
                            </span>
                          )}
                          <OldPrice svc={svc} className="text-base" />
                          {formatEuro(svc.price_onsite)}
                          <PromoBadge svc={svc} className="ml-2" />
                        </p>
                        <span className="mt-3 inline-flex w-fit items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
                          Demander un devis
                          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {weddingOptions.length > 0 && (
            <>
              <motion.div {...fadeUp} className="mt-10">
                <h3 className="font-heading text-lg font-semibold">
                  <span className="text-primary">Options</span> — personnalisez votre formule
                </h3>
              </motion.div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {weddingOptions.map((svc) => {
                  const Icon = SERVICE_ICONS[svc.icon] || Film;
                  return (
                    <motion.div key={svc.id} {...fadeUp}>
                      <Card
                        className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                        data-testid={`wedding-option-${svc.slug}`}
                      >
                        <CardContent className="flex h-full flex-col p-5">
                          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                            <Icon className="h-4.5 w-4.5 text-primary" />
                          </span>
                          <h4 className="mt-3 font-heading text-sm font-semibold">{svc.name}</h4>
                          <p className="mt-1 flex-1 text-xs text-muted-foreground">{svc.description}</p>
                          <p className="mt-3 font-mono-nums text-lg font-semibold text-primary">
                            +{" "}
                            {svc.from_price && (
                              <span className="text-xs font-normal text-muted-foreground">
                                à partir de{" "}
                              </span>
                            )}
                            <OldPrice svc={svc} />
                            {formatEuro(svc.price_onsite)}
                            <PromoBadge svc={svc} className="ml-1.5" />
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}

          <motion.div {...fadeUp} className="mt-10">
            <div
              className="rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-6 sm:p-8"
              data-testid="wedding-quote-banner"
            >
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <div>
                  <h3 className="font-heading text-lg font-semibold">
                    Chaque mariage est unique — <span className="text-primary">devis personnalisé gratuit</span>
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground/90">
                    Les prestations mariage se font uniquement sur devis. Remplissez notre{" "}
                    <Link
                      to="/devis-mariage"
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      data-testid="wedding-quote-form-link"
                    >
                      formulaire de demande de devis
                    </Link>{" "}
                    ou écrivez-nous à{" "}
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      data-testid="wedding-quote-email-link"
                    >
                      {CONTACT_EMAIL}
                    </a>{" "}
                    — nous vous répondrons rapidement avec une proposition adaptée.
                  </p>
                  <Button asChild className="mt-4" data-testid="wedding-quote-cta">
                    <Link to="/devis-mariage">
                      <Heart className="mr-2 h-4 w-4" />
                      Demander mon devis mariage
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="mt-5 flex flex-col gap-4 border-t border-primary/20 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3" data-testid="wedding-socials">
                  <span className="text-sm font-medium text-foreground/90">
                    Retrouvez-nous aussi sur nos réseaux :
                  </span>
                  <SocialBubbles />
                </div>
                {CONTACT_PHONE ? (
                  <a
                    href={CONTACT_PHONE_HREF}
                    className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                    data-testid="wedding-phone-link"
                  >
                    <Phone className="h-4 w-4" /> {CONTACT_PHONE}
                  </a>
                ) : (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="wedding-phone-soon">
                    <Phone className="h-4 w-4 text-primary" /> Bientôt joignables par téléphone
                  </span>
                )}
              </div>
              <p className="mt-5 text-sm leading-relaxed text-foreground/85" data-testid="wedding-passion-text">
                <span className="font-semibold text-primary">Professionnels et parfaitement équipés</span>,
                nous restons avant tout proches de vous et de vos envies. La photo et la vidéo sont notre
                passion — nous en avons fait notre raison de vivre. Que ce soit pour un mariage, un clip ou
                un podcast : <span className="font-medium">avant toute chose, nous sommes ici pour vous.</span>
              </p>
            </div>
          </motion.div>
        </section>
      )}

      {/* POST-PRODUCTION & ACCOMPAGNEMENT MUSICAL — compléments de session */}
      {IS_STUDIO && [
        {
          id: "post-production",
          title: "Post-production",
          subtitle: "Mixage et mastering professionnels pour finaliser vos morceaux.",
          items: complements.filter((s) => POST_PROD_SLUGS.includes(s.slug)),
        },
        {
          id: "accompagnement",
          title: "Accompagnement musical",
          subtitle: "Topline, ghostwriting et conseils pour aller au bout de votre idée.",
          items: complements.filter((s) => !POST_PROD_SLUGS.includes(s.slug)),
        },
      ].map((section) =>
        section.items.length > 0 ? (
          <section
            key={section.id}
            id={section.id}
            className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8"
          >
            <motion.div {...fadeUp}>
              <Badge variant="outline" className="border-primary/40 text-primary">
                Compléments de session
              </Badge>
              <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
                {section.title}
              </h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">{section.subtitle}</p>
              <div
                className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
                data-testid={`complements-notice-${section.id}`}
              >
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <p className="text-sm text-foreground/90">
                  <span className="font-semibold text-primary">Important :</span> ces prestations sont
                  des <span className="font-medium">compléments</span> — elles se réservent uniquement
                  avec une <span className="font-medium">session d'enregistrement d'1h minimum</span>,
                  payables en ligne comme sur place.
                </p>
              </div>
            </motion.div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {section.items.map((svc) => {
                const Icon = SERVICE_ICONS[svc.icon] || Mic;
                return (
                  <motion.div key={svc.id} {...fadeUp}>
                    <Card
                      className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                      data-testid={`complement-card-${svc.slug}`}
                    >
                      <CardContent className="flex h-full flex-col p-5 sm:p-6">
                        <div className="flex items-start justify-between">
                          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                            <Icon className="h-5 w-5 text-primary" />
                          </span>
                          <Badge variant="outline" className="border-primary/40 text-[11px] text-primary">
                            Avec une session
                          </Badge>
                        </div>
                        <h3 className="mt-4 font-heading text-base font-semibold">{svc.name}</h3>
                        <p className="mt-1 flex-1 text-xs text-muted-foreground">{svc.description}</p>
                        <p className="mt-4 font-mono-nums text-xl font-semibold text-primary">
                          {svc.from_price && (
                            <span className="mr-1 text-xs font-normal text-muted-foreground">
                              à partir de
                            </span>
                          )}
                          <OldPrice svc={svc} />
                          {formatEuro(svc.price_onsite)}
                          <PromoBadge svc={svc} className="ml-1.5" />
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            <motion.div {...fadeUp} className="mt-6">
              <Button asChild data-testid={`complements-booking-cta-${section.id}`}>
                <Link to="/reserver">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Réserver une session + compléments
                </Link>
              </Button>
            </motion.div>
          </section>
        ) : null
      )}

      {/* BEATMAKING (site studio uniquement) */}
      {IS_STUDIO && (
      <section id="beatmaking" className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <motion.div {...fadeUp}>
          <Badge variant="outline" className="border-primary/40 text-primary">
            {SITE.fullName}
          </Badge>
          <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            Beatmaking : catalogue &amp; sur mesure
          </h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Des prods exclusives disponibles au studio et des compositions sur mesure, à des tarifs
            plus accessibles que le marché — sans compromis sur la qualité.
          </p>
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4" data-testid="beatmaking-solfege-banner">
            <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-primary">Notre différence :</span> nos beatmakers
              travaillent aux côtés d'un <span className="font-medium">musicien diplômé en solfège</span>,
              pour des compositions harmoniquement irréprochables.
            </p>
          </div>
        </motion.div>

        {/* Phase A */}
        <motion.div {...fadeUp} className="mt-10">
          <h3 className="font-heading text-lg font-semibold">
            <span className="text-primary">Phase A</span> — Prods du catalogue (disponibles au studio)
          </h3>
        </motion.div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {beatCatalogue.map((svc) => {
            const Icon = SERVICE_ICONS[svc.icon] || Disc3;
            const isExclu = svc.slug === "exclusivite-instru";
            return (
              <motion.div key={svc.id} {...fadeUp}>
                <Card
                  className={`h-full bg-card transition-colors duration-200 hover:border-primary/60 ${
                    isExclu ? "border-primary/50" : "border-border"
                  }`}
                  data-testid={`beat-card-${svc.slug}`}
                >
                  <CardContent className="flex h-full flex-col p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                        <Icon className="h-5 w-5 text-primary" />
                      </span>
                      {isExclu && <Badge className="bg-primary text-primary-foreground">Exclusivité</Badge>}
                    </div>
                    <h4 className="mt-4 font-heading text-base font-semibold">{svc.name}</h4>
                    <p className="mt-1 text-xs text-muted-foreground">{svc.description}</p>
                    <ul className="mt-4 flex-1 space-y-1.5">
                      {(svc.features || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-foreground/85">
                          <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5">
                      <PriceRow svc={svc} />
                    </div>
                    <Button asChild size="sm" className="mt-4" data-testid={`beat-book-${svc.slug}`}>
                      <Link to="/reserver?studio=enregistrement">Réserver</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
          {/* Production instrumentale — affichage seul, non commandable */}
          <motion.div {...fadeUp}>
            <Card
              className="h-full border-dashed border-primary/40 bg-card"
              data-testid="beat-card-production-instrumentale"
            >
              <CardContent className="flex h-full flex-col p-5 sm:p-6">
                <div className="flex items-start justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                    <Wand2 className="h-5 w-5 text-primary" />
                  </span>
                  <Badge variant="outline" className="border-primary/40 text-[11px] text-primary">
                    Sur mesure
                  </Badge>
                </div>
                <h4 className="mt-4 font-heading text-base font-semibold">Production instrumentale</h4>
                <p className="mt-1 flex-1 text-xs text-muted-foreground">
                  Composition d'une instru sur mesure par nos beatmakers, adaptée à votre univers et
                  à votre projet.
                </p>
                <p className="mt-4 font-mono-nums text-lg font-semibold text-primary">
                  De 50 € à 200 €{" "}
                  <span className="text-xs font-normal text-muted-foreground">selon projet</span>
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground" data-testid="prod-instru-contact-notice">
                  Non commandable en ligne — nous contacter pour plus d'informations.
                </p>
                <Button asChild size="sm" variant="outline" className="mt-4" data-testid="prod-instru-contact-btn">
                  <Link to="/contact">Nous contacter</Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="mt-10">
          <div
            className="flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
            data-testid="album-quote-banner"
          >
            <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-primary">Projet / album complet :</span>{" "}
              l'enregistrement d'un projet ou d'un album complet se fait{" "}
              <span className="font-medium">uniquement sur devis</span>, et uniquement par mail à{" "}
              <a
                href="mailto:fnkfilms.pro@gmail.com"
                className="font-medium text-primary underline-offset-2 hover:underline"
                data-testid="album-quote-email-link"
              >
                fnkfilms.pro@gmail.com
              </a>
              .
            </p>
          </div>
        </motion.div>
      </section>
      )}

      {/* GALERIE (site films uniquement) */}
      {!IS_STUDIO && (
        <section id="galerie" className="border-t border-border bg-[hsl(var(--studio-ink))] noise-bg">
          <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <motion.div {...fadeUp}>
              <Badge variant="outline" className="border-primary/40 text-primary">
                {texts.galerie_badge || "Galerie"}
              </Badge>
              <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
                {texts.galerie_title || "Notre univers en images"}
              </h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                {realisationMedia.length > 0
                  ? texts.galerie_subtitle ||
                    "Explorez nos réalisations section par section : teasers, mariages, clips, photos, podcasts…"
                  : "Clips, mariages, podcasts, séances photo, drone… Un aperçu de ce que nous créons pour vous — nos propres réalisations arrivent très bientôt ici."}
              </p>
            </motion.div>
            {realisationMedia.length > 0 ? (
              <>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="gallery-filters">
                  <button
                    type="button"
                    onClick={() => setGalleryFilter("tous")}
                    className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                      galleryFilter === "tous"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/60"
                    }`}
                    data-testid="gallery-filter-tous"
                  >
                    Tous ({realisationMedia.length})
                  </button>
                  {galleryThemes.map((theme) => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => setGalleryFilter(theme)}
                      className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                        galleryFilter === theme
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-foreground hover:border-primary/60"
                      }`}
                      data-testid={`gallery-filter-${theme}`}
                    >
                      {MEDIA_CATEGORY_LABELS[theme]} ({realisationMedia.filter((m) => m.category === theme).length})
                    </button>
                  ))}
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredGalleryMedia.map((item) => (
                    <motion.div key={item.id} {...fadeUp}>
                      <div
                        className="group overflow-hidden rounded-xl border border-primary/20 bg-black"
                        data-testid={`gallery-media-${item.id}`}
                      >
                        <div className="relative">{renderMedia(item, "aspect-video")}</div>
                        <p className="border-t border-primary/20 bg-black/80 px-3 py-2 text-xs font-semibold text-white">
                          {item.title || MEDIA_CATEGORY_LABELS[item.category] || ""}
                          <span className="ml-2 font-normal text-white/60">
                            {MEDIA_CATEGORY_LABELS[item.category]}
                          </span>
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-8 columns-2 gap-3 sm:columns-3 lg:columns-4 [&>div]:mb-3">
              {GALLERY_ITEMS.map((item) => (
                <motion.div key={item.label} {...fadeUp} className="break-inside-avoid">
                  <div
                    className="group relative overflow-hidden rounded-xl border border-primary/20"
                    data-testid={`gallery-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <img
                      src={item.src}
                      alt={item.label}
                      loading="lazy"
                      className={`w-full object-cover transition-transform duration-300 group-hover:scale-[1.05] ${
                        item.tall ? "aspect-[3/4]" : "aspect-[4/3]"
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                    <p className="absolute bottom-2.5 left-3 right-3 text-xs font-semibold text-white">
                      {item.label}
                    </p>
                  </div>
                </motion.div>
              ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* COMMENT ÇA MARCHE + REGLES */}
      <section className="border-t border-border bg-[hsl(var(--studio-ink))] noise-bg">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <motion.h2 {...fadeUp} className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            {texts.steps_title || "Comment ça marche"}
          </motion.h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                step: "01",
                title: "Choisissez votre créneau",
                text: "Consultez le planning de chaque espace et sélectionnez la date et l'heure qui vous conviennent, 24h/24.",
              },
              {
                step: "02",
                title: "Payez en ligne ou sur place",
                text: "Le paiement en ligne confirme définitivement votre créneau. Sur place, votre demande est validée par notre équipe et confirmée par email. Même tarif dans les deux cas.",
              },
              {
                step: "03",
                title: "Présentez-vous au studio",
                text: "Vous recevez un email de confirmation avec toutes les infos. Venez avec votre projet, on s'occupe du reste.",
              },
            ].map((item) => (
              <motion.div key={item.step} {...fadeUp}>
                <div className="h-full rounded-xl border border-border bg-card p-6">
                  <span className="font-mono-nums text-sm font-semibold text-primary">{item.step}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-12 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-8 text-center sm:p-10">
            <h3 className="font-heading text-xl font-semibold sm:text-2xl">
              {IS_STUDIO
                ? "Prêt à enregistrer votre prochain projet ?"
                : texts.cta_title || "Prêt à donner vie à votre projet en images ?"}
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
              {texts.cta_subtitle || "Réservez dès maintenant — paiement en ligne sécurisé ou sur place au studio."}
            </p>
            <Button size="lg" className="mt-6" asChild data-testid="bottom-booking-cta">
              <Link to="/reserver">
                <CalendarDays className="mr-2 h-5 w-5" />
                Réserver un créneau
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
